import {
  SHStack,
  SHToggleButtonOptions,
  SHTypography,
} from "@components/design-systems";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import {
  AnalysisSummaryDTO,
  RenderAnalysisSummaryItemProps,
} from "@models/platform-analysis/entities/steps/summary";
import { TableViewMode } from "@models/platform-analysis/enums/tableViewMode";
import { Collapse } from "@mui/material";
import { RootState } from "@redux/store";
import { isEqual } from "lodash";
import { memo, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useToggle } from "react-use";
import { SummaryFeaturesIncludeTable } from "./table";

export const SummaryFeaturesIncluded = memo(
  ({ isReview = false }: RenderAnalysisSummaryItemProps) => {
    const { control } = useFormContext<AnalysisSummaryDTO>();
    const { isCompleted, isReadOnly } = usePlatformAnalysis();

    const hasFeatureStep = useWatch({
      control,
      name: "hasFeatureStep",
    });

    const selectedFeatures = useSelector(
      (state: RootState) =>
        state.platformAnalysis.platformAnalysis?.summary?.selectedFeatures,
    );

    const storedTableViewMode = useSelector(
      (state: RootState) =>
        state.platformAnalysis.internal.summary?.featureAnalysisTableViewMode,
    );

    const [isCollapsed, toggleCollapse] = useToggle(
      storedTableViewMode === TableViewMode.Detail,
    );

    const isDetailMode = useMemo(
      () => isEqual(storedTableViewMode, TableViewMode.Detail),
      [storedTableViewMode],
    );

    //#region Render
    if (isDetailMode || !hasFeatureStep || isCompleted || isReadOnly) {
      return <></>;
    }

    return (
      <>
        <Controller
          name="showSelectedFeatures"
          control={control}
          render={({ field: { value, onChange } }) => (
            <SHStack width={"100%"} gap={2}>
              <SHStack flexDirection={"row"} justifyContent={"space-between"}>
                <SHTypography variant="subtitle2">
                  Features included in the {isReview ? "review" : "analysis"}
                </SHTypography>

                <SHToggleButtonOptions
                  options={[
                    {
                      label: "Show",
                      value: "true",
                    },
                    {
                      label: "Hide",
                      value: "false",
                    },
                  ]}
                  value={value.toString()}
                  onChange={(_, value: string) => {
                    onChange(value === "true");
                    toggleCollapse(value !== "true");
                  }}
                />
              </SHStack>
            </SHStack>
          )}
        />

        <Collapse in={!isCollapsed}>
          <SummaryFeaturesIncludeTable
            selectedFeatures={selectedFeatures ?? []}
          />
        </Collapse>
      </>
    );
  },
);
